import React from "react";
import { Box, Typography, Chip, useTheme } from "@mui/material";

/**
 * Modern, Professional PageHeader Component
 * 
 * Clean, compact fintech-style page header that seamlessly integrates into both
 * mobile and desktop views without wasting precious vertical screen height.
 * 
 * @param {Object} props
 * @param {string} props.title - Primary heading text
 * @param {string} props.subtitle - Supporting description / tagline
 * @param {React.ReactNode} props.icon - Icon element for the header
 * @param {string} props.badgeText - Pill chip badge text (e.g. "Split & Groups")
 * @param {string} props.gradient - Optional custom gradient for the icon container
 * @param {React.ReactNode} props.action - Optional right-side action slot
 */
const PageHeader = ({
  title,
  subtitle,
  icon,
  badgeText,
  gradient,
  action,
  sx = {},
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const defaultGradient = isDark
    ? "linear-gradient(135deg, rgba(99, 102, 241, 0.22) 0%, rgba(139, 92, 246, 0.28) 100%)"
    : "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(139, 92, 246, 0.15) 100%)";

  return (
    <Box
      sx={{
        width: "100%",
        mb: { xs: 1.5, sm: 2 },
        pb: { xs: 1.2, sm: 1.5 },
        pt: { xs: 0.2, sm: 0.5 },
        borderBottom: "1px solid",
        borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.2, sm: 1.5 }, minWidth: 0 }}>
        {/* Modern Compact Icon Container */}
        {icon && (
          <Box
            sx={{
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: gradient || defaultGradient,
              border: "1px solid",
              borderColor: isDark ? "rgba(129, 140, 248, 0.25)" : "rgba(99, 102, 241, 0.2)",
              color: isDark ? "#c7d2fe" : "#4f46e5",
              boxShadow: isDark
                ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                : "0 2px 8px rgba(99, 102, 241, 0.08)",
              flexShrink: 0,
              "& svg": {
                fontSize: { xs: 20, sm: 22 },
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Title + Subtitle */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              component="h1"
              sx={{
                fontWeight: 750,
                fontSize: { xs: "1.1rem", sm: "1.35rem" },
                letterSpacing: "-0.025em",
                color: isDark ? "#f8fafc" : "#0f172a",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </Typography>

            {badgeText && (
              <Chip
                size="small"
                label={badgeText}
                sx={{
                  height: 18,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  borderRadius: "5px",
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.18)" : "rgba(99, 102, 241, 0.08)",
                  color: isDark ? "#c7d2fe" : "#4f46e5",
                  border: "1px solid",
                  borderColor: isDark ? "rgba(129, 140, 248, 0.25)" : "rgba(99, 102, 241, 0.18)",
                  display: { xs: "none", sm: "inline-flex" },
                }}
              />
            )}
          </Box>

          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "#94a3b8" : "#64748b",
                fontWeight: 500,
                fontSize: { xs: "0.74rem", sm: "0.82rem" },
                lineHeight: 1.35,
                mt: 0.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: { xs: "280px", sm: "100%" },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Action / Slot */}
      {action && (
        <Box sx={{ flexShrink: 0 }}>
          {action}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
